import React from 'react';

const InvoicePreview = ({ data }) => {
  if (!data) return <p>No invoice data found.</p>;

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '0.00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

    const currencySymbols = {
      'USD': '$',
      'GBP': '£',
      'EUR': '€',
      'INR': '₹',
    };

    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const currency = data.currency || 'USD';
  const subtotal = data.subtotal || 0;
  const tax = data.tax || 0;
  const total = data.total || 0;

  // ✅ Safely get items array
  const items = Array.isArray(data.items) ? data.items : [];

  console.log('[InvoicePreview] Data received:', data); // Debug log
  console.log('[InvoicePreview] Items:', items); // Debug log

  return (
    <div style={styles.container}>
      <div style={styles.invoiceWrapper}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <div>
              <h1 style={styles.title}>INVOICE</h1>
              <p style={styles.invoiceNum}>#{data.invoice_number || 'N/A'}</p>
            </div>
            <div style={styles.headerRight}>
              <div style={styles.statusBadge}>
                <span>📋 Active Invoice</span>
              </div>
            </div>
          </div>
        </div>

        {/* Company & Client Info */}
        <div style={styles.infoSection}>
          <div style={styles.infoBlock}>
            <p style={styles.label}>FROM</p>
            <p style={styles.companyName}>{data.vendor || 'Vendor Information'}</p>
            <p style={styles.detail}>Vendor details would appear here</p>
          </div>

          <div style={styles.infoBlock}>
            <p style={styles.label}>BILL TO</p>
            <p style={styles.companyName}>{data.client || 'Client Information'}</p>
            <p style={styles.detail}>Client details would appear here</p>
          </div>

          <div style={styles.infoBlock}>
            <p style={styles.label}>INVOICE DETAILS</p>
            <div style={styles.detailRow}>
              <span>Issue Date:</span>
              <span style={styles.detailValue}>{formatDate(data.issue_date)}</span>
            </div>
            <div style={styles.detailRow}>
              <span>Due Date:</span>
              <span style={styles.detailValue}>{formatDate(data.due_date)}</span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div style={styles.itemsSection}>
          <h3 style={styles.sectionTitle}>Line Items</h3>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={{ ...styles.cell, ...styles.itemNameCell }}>Item Name</th>
                  <th style={{ ...styles.cell, ...styles.descriptionCell }}>Description</th>
                  <th style={{ ...styles.cell, textAlign: 'center' }}>Quantity</th>
                  <th style={{ ...styles.cell, textAlign: 'right' }}>Unit Price</th>
                  <th style={{ ...styles.cell, textAlign: 'right', ...styles.amountCell }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {items && items.length > 0 ? (
                  items.map((item, idx) => {
                    // ✅ FIX: Handle different data formats
                    const itemName = item.name ;
                    const itemDescription = item.description || '';
                    const quantity = item.quantity || 1;
                    const unitPrice = item.unit_price || 0;
                    const amount = item.total || item.amount || (quantity * unitPrice);

                    return (
                      <tr key={idx} style={styles.tableRow}>
                        <td style={{ ...styles.cell, ...styles.itemNameCell }}>
                          <p style={styles.itemName}>{itemName}</p>
                        </td>
                        <td style={{ ...styles.cell, ...styles.descriptionCell }}>
                          <p style={styles.description}>{itemDescription}</p>
                        </td>
                        <td style={{ ...styles.cell, textAlign: 'center' }}>
                          {quantity}
                        </td>
                        <td style={{ ...styles.cell, textAlign: 'right' }}>
                          {formatCurrency(unitPrice, currency)}
                        </td>
                        <td style={{ ...styles.cell, textAlign: 'right', ...styles.amountCell }}>
                          <strong>{formatCurrency(amount, currency)}</strong>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" style={{ ...styles.cell, textAlign: 'center', color: '#999' }}>
                      No items found in invoice
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Section */}
        <div style={styles.summarySection}>
          <div style={styles.summaryBlank}></div>
          <div style={styles.summaryBox}>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Subtotal:</span>
              <span style={styles.summaryValue}>{formatCurrency(subtotal, currency)}</span>
            </div>
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Tax / VAT:</span>
              <span style={styles.summaryValue}>{formatCurrency(tax, currency)}</span>
            </div>
            <div style={{...styles.summaryRow, borderTop: '2px solid #667eea', paddingTop: '12px', marginTop: '12px'}}>
              <span style={{ ...styles.summaryLabel, fontWeight: 'bold', fontSize: '16px' }}>Total:</span>
              <span style={{ ...styles.summaryValue, fontWeight: 'bold', fontSize: '18px', color: '#667eea' }}>
                {formatCurrency(total, currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p style={styles.footerText}>Thank you for your business</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#f5f7fa',
    borderRadius: '12px',
    marginTop: '20px',
  },
  invoiceWrapper: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '40px',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: '36px',
    fontWeight: 'bold',
    margin: '0 0 5px 0',
    letterSpacing: '2px',
  },
  invoiceNum: {
    fontSize: '14px',
    opacity: 0.9,
    margin: 0,
  },
  headerRight: {
    textAlign: 'right',
  },
  statusBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backdropFilter: 'blur(10px)',
  },
  infoSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '30px',
    padding: '40px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#fafbfc',
  },
  infoBlock: {
    flex: 1,
  },
  label: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#667eea',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    margin: '0 0 8px 0',
  },
  companyName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a202c',
    margin: '0 0 6px 0',
  },
  detail: {
    fontSize: '13px',
    color: '#718096',
    margin: 0,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#4a5568',
    marginBottom: '6px',
  },
  detailValue: {
    fontWeight: '500',
    color: '#1a202c',
  },
  itemsSection: {
    padding: '40px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1a202c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 20px 0',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f7fafc',
    borderBottom: '2px solid #667eea',
  },
  cell: {
    padding: '14px',
    fontSize: '13px',
    textAlign: 'left',
    borderBottom: '1px solid #eee',
  },
  descriptionCell: {
    width: '40%',
  },
  itemNameCell: {
    width: '25%',
  },
  amountCell: {
    fontWeight: '600',
  },
  tableRow: {
    transition: 'background-color 0.2s',
  },
  itemName: {
    margin: 0,
    color: '#1a202c',
    fontWeight: '600',
  },
  description: {
    margin: 0,
    color: '#718096',
    fontWeight: '400',
    fontSize: '12px',
  },
  summarySection: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: '40px',
    padding: '40px',
    borderTop: '1px solid #eee',
    backgroundColor: '#fafbfc',
  },
  summaryBlank: {
    flex: 1,
  },
  summaryBox: {
    backgroundColor: 'white',
    border: '2px solid #667eea',
    borderRadius: '8px',
    padding: '24px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    fontSize: '14px',
  },
  summaryLabel: {
    color: '#718096',
    fontWeight: '500',
  },
  summaryValue: {
    color: '#1a202c',
    fontWeight: '600',
  },
  footer: {
    padding: '30px 40px',
    backgroundColor: '#f7fafc',
    textAlign: 'center',
    borderTop: '1px solid #eee',
  },
  footerText: {
    margin: 0,
    fontSize: '13px',
    color: '#718096',
    fontWeight: '500',
  },
};

export default InvoicePreview;